import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPatientReferrerDirectory } from '@/services/patientReferrerDirectory';
import { ReferrerSearchSelect } from '@/components/ReferrerSearchSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Send, Loader2, CheckCircle2, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LEAD_SOURCE_OPTIONS, DEFAULT_MANUAL_LEAD_SOURCE } from '@/lib/leadSource';
import { getPatientOwnerChoices } from '@/lib/patientOwners';
import { canSavePatientCase, getPatientCaseFormMode } from '@/lib/patientCaseForm';

// No `completed`: Completed and Referred Back are one status, `referred_back`
// (Samuel, 29 Sep 2026). Old rows are mapped by normalizePatientCaseStatus().
export type PatientCaseUiStatus =
  | 'new_referral'
  | 'appointment_scheduled'
  | 'patient_seen'
  | 'treatment_proposed'
  | 'treatment_accepted'
  | 'treatment_ongoing'
  | 'no_show'
  | 'non_compliant'
  | 'referred_back'
  | 'referred_to_other_specialty'
  | 'patient_did_not_proceed'
  | 'lost_inactive';

export type PatientCaseConversionStatus = 'pending' | 'converted' | 'not_converted' | 'inactive';

export interface PatientCaseTreatmentRequest {
  key: string;
  label: string;
  selected: boolean;
  toothNumber: string;
}

export interface PatientCaseUiRecord {
  id: string;
  patientDisplayName: string;
  patientNric?: string | null;
  patientPhone?: string | null;
  platoReferenceId: string;
  // The short patient number staff see in Plato (e.g. 19806). Display only;
  // platoReferenceId stays the internal key every Plato join uses.
  platoPatientNumber?: string | null;
  clinicEmail?: string | null;
  clinicBranch?: string | null;
  preferredSpecialist?: string | null;
  treatmentRequests?: PatientCaseTreatmentRequest[];
  referralFormVersion?: string | null;
  submissionNote?: string | null;
  referringDoctor: string;
  referringClinic: string;
  referringDoctorId?: string | null;
  referringClinicId?: string | null;
  referralChannel: string;
  source?: string | null;
  referralReceivedDate: string | null;
  clinicId: string;
  clinicName: string;
  status: PatientCaseUiStatus;
  conversionStatus: PatientCaseConversionStatus;
  conversionDate: string | null;
  nextAction: string;
  nextActionDueDate: string | null;
  assignedOwnerStaffId: string | null;
  assignedOwnerName: string | null;
  referredBackFlag: boolean;
  referredBackDate: string | null;
  remarks: string;
  externalStatusNote: string;
  statusChangedAt: string;
  treatmentAcceptedAt: string | null;
  completedAt: string | null;
  inactiveAt: string | null;
  createdAt: string;
  updatedAt: string;
  platoWaitlistPushedAt?: string | null;
  platoWaitlistDate?: string | null;
  platoWaitlistLocation?: string | null;
}

export interface PatientCaseOwnerOption {
  id: string;
  name: string;
  role?: string;
}

export interface PatientCaseFormValues {
  patientDisplayName: string;
  platoReferenceId: string;
  referringDoctor: string;
  referringClinic: string;
  referringDoctorId?: string | null;
  referringClinicId?: string | null;
  referralChannel: string;
  source: string;
  referralReceivedDate: string | null;
  status: PatientCaseUiStatus;
  conversionStatus: PatientCaseConversionStatus;
  conversionDate: string | null;
  nextAction: string;
  nextActionDueDate: string | null;
  assignedOwnerStaffId: string | null;
  referredBackFlag: boolean;
  referredBackDate: string | null;
  remarks: string;
  externalStatusNote: string;
}

interface PatientCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: PatientCaseFormValues) => void;
  onDelete?: (patientCase: PatientCaseUiRecord) => void;
  isDeleting?: boolean;
  owners: PatientCaseOwnerOption[];
  clinicName: string;
  patientCase?: PatientCaseUiRecord | null;
  onWaitlistPushed?: (caseId: string, location: string, date: string) => void;
}

const STATUS_OPTIONS: Array<{ value: PatientCaseUiStatus; label: string }> = [
  { value: 'new_referral', label: 'New Referral' },
  { value: 'appointment_scheduled', label: 'Appointment Scheduled' },
  { value: 'patient_seen', label: 'Patient Seen' },
  { value: 'treatment_proposed', label: 'Treatment Proposed' },
  { value: 'treatment_accepted', label: 'Treatment Accepted' },
  { value: 'treatment_ongoing', label: 'Treatment Ongoing' },
  { value: 'referred_to_other_specialty', label: 'Referred to Other Specialty' },
  { value: 'no_show', label: 'No Show' },
  { value: 'non_compliant', label: 'Non-compliant / Not Returning' },
  { value: 'referred_back', label: 'Referred Back' },
  { value: 'patient_did_not_proceed', label: 'Patient Did Not Proceed' },
  { value: 'lost_inactive', label: 'Lost / Inactive' },
];

const REFERRAL_CHANNELS = ['phone', 'email', 'walk-in', 'online', 'manual', 'other'] as const;

// Placeholder reference for manual (WhatsApp / phone) referrals logged before a
// Plato record exists. Mirrors the external portal's convention:
// 'PENDING-' || upper(substr(gen_random_uuid()::text, 1, 8))
// (see submit_external_referral in migration 20260430100000). Plato sync flows
// key on plato_appointment_id / real Plato ids, so PENDING ids are never pushed.
const generatePendingReferenceId = (): string => {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `PENDING-${hex.toUpperCase()}`;
};

const isReferralLoopCompleteStatus = (status: PatientCaseUiStatus) =>
  status === 'referred_back';

const getReferralLoopLabel = (status: PatientCaseUiStatus) => {
  if (isReferralLoopCompleteStatus(status)) {
    return 'Referred back to clinic';
  }

  if (['no_show', 'patient_did_not_proceed', 'lost_inactive', 'non_compliant'].includes(status)) {
    return 'NIL';
  }

  return 'Ongoing treatment';
};

const emptyForm = (): PatientCaseFormValues => ({
  patientDisplayName: '',
  platoReferenceId: '',
  referringDoctor: '',
  referringClinic: '',
  referringDoctorId: null,
  referringClinicId: null,
  referralChannel: 'email',
  source: DEFAULT_MANUAL_LEAD_SOURCE,
  referralReceivedDate: null,
  status: 'new_referral',
  conversionStatus: 'pending',
  conversionDate: null,
  nextAction: '',
  nextActionDueDate: null,
  assignedOwnerStaffId: null,
  referredBackFlag: false,
  referredBackDate: null,
  remarks: '',
  externalStatusNote: '',
});

type WaitlistLocation = 'TCENDO' | 'SSD';

const WAITLIST_LOCATIONS: Array<{ value: WaitlistLocation; label: string }> = [
  { value: 'TCENDO', label: 'TCENDO' },
  { value: 'SSD', label: 'SSD' },
];

const formatWaitlistDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PatientCaseModal: React.FC<PatientCaseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  isDeleting,
  owners,
  clinicName,
  patientCase,
  onWaitlistPushed,
}) => {
  const [formValues, setFormValues] = useState<PatientCaseFormValues>(emptyForm);
  const [isManualReferral, setIsManualReferral] = useState(false);
  const [waitlistLocation, setWaitlistLocation] = useState<WaitlistLocation>('TCENDO');
  const [isPushingWaitlist, setIsPushingWaitlist] = useState(false);
  const [waitlistPushedAt, setWaitlistPushedAt] = useState<string | null>(null);
  const [waitlistDate, setWaitlistDate] = useState<string | null>(null);
  const [waitlistPushedLocation, setWaitlistPushedLocation] = useState<string | null>(null);
  const { toast } = useToast();
  const ownerChoices = getPatientOwnerChoices(owners, patientCase);
  const directoryQuery = useQuery({
    queryKey: ['patient-referrer-directory'],
    queryFn: getPatientReferrerDirectory,
    enabled: isOpen,
    staleTime: 60_000,
  });
  const directory = directoryQuery.data ?? [];
  const clinicOptions = [...new Map(directory.map((row) => [row.clinic_id, { id: row.clinic_id, label: row.clinic_name }])).values()]
    .sort((a, b) => a.label.localeCompare(b.label));
  const doctorOptions = directory.filter((row) => row.doctor_id && row.doctor_name && row.doctor_name !== '[Blank]')
    .map((row) => ({ id: row.doctor_id!, label: `${row.doctor_name} — ${row.clinic_name}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const selectReferringDoctor = (id: string | null) => {
    const doctor = directory.find((row) => row.doctor_id === id && id !== null);
    setFormValues((current) => ({ ...current,
      referringDoctorId: doctor?.doctor_id ?? null,
      referringDoctor: doctor?.doctor_name ?? '',
      ...(doctor ? { referringClinicId: doctor.clinic_id, referringClinic: doctor.clinic_name } : {}),
    }));
  };
  const selectReferringClinic = (id: string | null) => {
    const clinic = directory.find((row) => row.clinic_id === id);
    setFormValues((current) => {
      const matchingDoctor = directory.some((row) => row.doctor_id === current.referringDoctorId && row.clinic_id === id);
      return { ...current, referringClinicId: clinic?.clinic_id ?? null, referringClinic: clinic?.clinic_name ?? '',
        ...(!matchingDoctor ? { referringDoctorId: null, referringDoctor: '' } : {}),
      };
    });
  };


  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (patientCase) {
      // Coalesce nullable text fields to '' — portal/website/Plato-synced cases
      // can leave these null, and the form (and handleSave's .trim()) expect
      // strings. A null here previously crashed the edit modal.
      setFormValues({
        patientDisplayName: patientCase.patientDisplayName ?? '',
        platoReferenceId: patientCase.platoReferenceId ?? '',
        referringDoctor: patientCase.referringDoctor ?? '',
        referringClinic: patientCase.referringClinic ?? '',
        referringDoctorId: patientCase.referringDoctorId,
        referringClinicId: patientCase.referringClinicId,
        referralChannel: patientCase.referralChannel,
        source: patientCase.source ?? DEFAULT_MANUAL_LEAD_SOURCE,
        referralReceivedDate: patientCase.referralReceivedDate,
        status: patientCase.status,
        conversionStatus: patientCase.conversionStatus,
        conversionDate: patientCase.conversionDate,
        nextAction: patientCase.nextAction ?? '',
        nextActionDueDate: patientCase.nextActionDueDate,
        assignedOwnerStaffId: patientCase.assignedOwnerStaffId,
        referredBackFlag: patientCase.referredBackFlag,
        referredBackDate: patientCase.referredBackDate,
        remarks: patientCase.remarks ?? '',
        externalStatusNote: patientCase.externalStatusNote ?? '',
      });
      setIsManualReferral(false);
      setWaitlistPushedAt(patientCase.platoWaitlistPushedAt ?? null);
      setWaitlistDate(patientCase.platoWaitlistDate ?? null);
      setWaitlistPushedLocation(patientCase.platoWaitlistLocation ?? null);
      return;
    }

    setFormValues(emptyForm());
    setIsManualReferral(false);
    setWaitlistPushedAt(null);
    setWaitlistDate(null);
    setWaitlistPushedLocation(null);
  }, [isOpen, patientCase]);

  const handleChange = <K extends keyof PatientCaseFormValues>(
    key: K,
    value: PatientCaseFormValues[K]
  ) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [key]: value,
    }));
  };

  // Manual (WhatsApp / phone) referrals are only a create-time concept; edits
  // keep whatever reference the case already has.
  const manualReferralActive = !patientCase && isManualReferral;

  const handleManualReferralToggle = (checked: boolean) => {
    setIsManualReferral(checked);
    setFormValues((currentValues) => ({
      ...currentValues,
      // Default the channel to 'manual' when checked (user can still change it);
      // revert the default when unchecked if the user didn't pick something else.
      referralChannel: checked
        ? 'manual'
        : currentValues.referralChannel === 'manual'
          ? 'email'
          : currentValues.referralChannel,
    }));
  };

  const canSave = canSavePatientCase(formValues, getPatientCaseFormMode(patientCase, isManualReferral));

  const handleSave = () => {
    if (!canSave) return;

    const platoReferenceId = manualReferralActive && !formValues.platoReferenceId.trim()
      ? generatePendingReferenceId()
      : (formValues.platoReferenceId ?? '').trim();

    const isReferralLoopComplete = isReferralLoopCompleteStatus(formValues.status);
    const normalizedValues: PatientCaseFormValues = {
      ...formValues,
      patientDisplayName: (formValues.patientDisplayName ?? '').trim(),
      platoReferenceId,
      referringDoctor: (formValues.referringDoctor ?? '').trim(),
      referringClinic: (formValues.referringClinic ?? '').trim(),
      nextAction: (formValues.nextAction ?? '').trim(),
      remarks: (formValues.remarks ?? '').trim(),
      externalStatusNote: (formValues.externalStatusNote ?? '').trim(),
      referredBackFlag: isReferralLoopComplete,
      referredBackDate: isReferralLoopComplete ? formValues.referredBackDate : null,
    };

    onSave(normalizedValues);
  };

  const handlePushToWaitlist = async () => {
    if (!patientCase?.id) return;

    setIsPushingWaitlist(true);
    try {
      const { data, error } = await supabase.functions.invoke('plato-waitlist-push', {
        body: {
          patient_case_id: patientCase.id,
          location: waitlistLocation,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error ?? 'Waitlist push failed');
      }

      const pushedAt = new Date().toISOString();
      const pushedDate = data?.waitlist_date ?? pushedAt;

      setWaitlistPushedAt(pushedAt);
      setWaitlistDate(pushedDate);
      setWaitlistPushedLocation(waitlistLocation);

      onWaitlistPushed?.(patientCase.id, waitlistLocation, pushedDate);

      toast({
        title: 'Pushed to Waitlist',
        description: `${patientCase.patientDisplayName} added to ${waitlistLocation} waitlist.`,
      });
    } catch (err) {
      console.error('[PatientCaseModal] Waitlist push error:', err);
      toast({
        title: 'Waitlist push failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsPushingWaitlist(false);
    }
  };

  const referralLoopComplete = isReferralLoopCompleteStatus(formValues.status);
  const isAlreadyPushedToWaitlist = Boolean(waitlistPushedAt);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-1">
              <DialogTitle>{patientCase ? 'Edit Patient Case' : 'New Patient Case'}</DialogTitle>
              <DialogDescription>
                Operational case tracking only. Plato remains the source of truth for the full patient record.
              </DialogDescription>
            </div>
            <Badge variant="outline" className="hidden sm:inline-flex">
              {clinicName}
            </Badge>
          </div>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="patientDisplayName">Patient Display Name</Label>
              <Input
                id="patientDisplayName"
                value={formValues.patientDisplayName}
                onChange={(event) => handleChange('patientDisplayName', event.target.value)}
                placeholder="e.g. Jeremy Tan"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="platoReferenceId">
                Plato reference
                {manualReferralActive && (
                  <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
                )}
              </Label>
              <Input
                id="platoReferenceId"
                value={formValues.platoReferenceId}
                onChange={(event) => handleChange('platoReferenceId', event.target.value)}
                placeholder={manualReferralActive ? 'auto-generated' : 'e.g. PL-20481'}
                disabled={manualReferralActive}
              />
            </div>
          </div>

          {patientCase && (
            <p className="text-sm text-slate-600">
              ID <span className="font-medium text-slate-900">{patientCase.platoPatientNumber || '-'}</span>
              <span className="ml-2 text-xs text-muted-foreground">(Plato patient number, filled from Plato)</span>
            </p>
          )}

          {!patientCase && (
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
              <Checkbox
                id="manualReferral"
                checked={isManualReferral}
                onCheckedChange={(checked) => handleManualReferralToggle(checked === true)}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label htmlFor="manualReferral" className="cursor-pointer text-sm font-normal">
                  Manual referral (WhatsApp / phone) — no ID yet
                </Label>
                <p className="text-xs text-muted-foreground">
                  A temporary PENDING reference is generated so the case can be logged before the
                  Plato record exists.
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="referringDoctor">Referring Doctor</Label>
              <ReferrerSearchSelect id="referringDoctor" label="Referring Doctor"
                selectedId={formValues.referringDoctorId} selectedLabel={formValues.referringDoctor}
                options={doctorOptions} disabled={directoryQuery.isLoading || directoryQuery.isError}
                onSelect={selectReferringDoctor} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="referringClinic">Referring Clinic</Label>
              <ReferrerSearchSelect id="referringClinic" label="Referring Clinic"
                selectedId={formValues.referringClinicId} selectedLabel={formValues.referringClinic}
                options={clinicOptions} disabled={directoryQuery.isLoading || directoryQuery.isError}
                onSelect={selectReferringClinic} />
              {directoryQuery.isError && <p role="alert" className="text-sm text-destructive">
                Could not load the referring directory. <button type="button" className="underline" onClick={() => directoryQuery.refetch()}>Retry</button>
              </p>}
            </div>
            <div className="space-y-2">
              <Label>Referral Channel</Label>
              <Select
                value={formValues.referralChannel}
                onValueChange={(value) => handleChange('referralChannel', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_CHANNELS.map((channel) => (
                    <SelectItem key={channel} value={channel}>
                      {channel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lead Source</Label>
              <Select
                value={formValues.source}
                onValueChange={(value) => handleChange('source', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Internal only — not shown to the referring dentist. Website &amp; Portal
                leads are tagged automatically.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Status</Label>
              <Select
                value={formValues.status}
                onValueChange={(value: PatientCaseUiStatus) => handleChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="referralReceivedDate">Referral Received</Label>
              <Input
                id="referralReceivedDate"
                type="date"
                value={formValues.referralReceivedDate ?? ''}
                onChange={(event) =>
                  handleChange('referralReceivedDate', event.target.value || null)
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned Owner</Label>
              <Select
                value={formValues.assignedOwnerStaffId ?? 'unassigned'}
                onValueChange={(value) =>
                  handleChange('assignedOwnerStaffId', value === 'unassigned' ? null : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {ownerChoices.map((owner) => (
                    <SelectItem key={owner.id} value={owner.id} disabled={owner.currentOnly}>
                      {owner.name}{owner.currentOnly ? ' (current assignment)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nextAction">Next Action</Label>
              <Input
                id="nextAction"
                value={formValues.nextAction}
                onChange={(event) => handleChange('nextAction', event.target.value)}
                placeholder="Awaiting doctor review"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nextActionDueDate">Next Action Due Date</Label>
              <Input
                id="nextActionDueDate"
                type="date"
                value={formValues.nextActionDueDate ?? ''}
                onChange={(event) =>
                  handleChange('nextActionDueDate', event.target.value || null)
                }
              />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
            <div className="space-y-3">
              <div>
                <Label>Referral loop</Label>
                <p className="text-sm text-muted-foreground">
                  {getReferralLoopLabel(formValues.status)}
                </p>
              </div>
              {referralLoopComplete && (
                <div className="space-y-2 sm:max-w-xs">
                  <Label htmlFor="referredBackDate">Referred Back Date</Label>
                  <Input
                    id="referredBackDate"
                    type="date"
                    value={formValues.referredBackDate ?? ''}
                    onChange={(event) =>
                      handleChange('referredBackDate', event.target.value || null)
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="remarks">Operational Remarks</Label>
            <Textarea
              id="remarks"
              rows={4}
              value={formValues.remarks}
              onChange={(event) => handleChange('remarks', event.target.value)}
              placeholder="Short operational notes only. No clinical records."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="externalStatusNote">External-safe Status Note</Label>
            <Textarea
              id="externalStatusNote"
              rows={3}
              value={formValues.externalStatusNote}
              onChange={(event) => handleChange('externalStatusNote', event.target.value)}
              placeholder="Simple update safe for a referring dentist to see."
            />
          </div>
        </div>

        {/* Push to Plato Waitlist */}
        {patientCase && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
            <Label className="text-sm font-medium">Plato Waitlist</Label>
            {isAlreadyPushedToWaitlist ? (
              <div className="mt-2">
                <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700 gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Pushed to WAITLIST ({waitlistPushedLocation})
                  {waitlistDate && ` · ${formatWaitlistDate(waitlistDate)}`}
                </Badge>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <Select
                  value={waitlistLocation}
                  onValueChange={(value) => setWaitlistLocation(value as WaitlistLocation)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {WAITLIST_LOCATIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePushToWaitlist}
                  disabled={isPushingWaitlist}
                >
                  {isPushingWaitlist ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {isPushingWaitlist ? 'Pushing...' : 'Push to Waitlist'}
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          {patientCase && onDelete ? (
            <Button
              variant="destructive"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete the referral for ${patientCase.patientDisplayName}? This removes it from the list and cannot be undone.`
                  )
                ) {
                  onDelete(patientCase);
                }
              }}
              disabled={isDeleting}
              className="sm:mr-auto"
            >
              {isDeleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave}
            >
              {patientCase ? 'Save Changes' : 'Create Case'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatientCaseModal;
